import { repAxios } from './representativeService';
import { GestoriSse, leggiSse } from './verbaliService';

// Audio del verbale (area associativa, piano 2): registrazione a pezzi, caricamento di un file,
// trascrizione, momenti segnati, voci e verbale generato dalla trascrizione. Chiamate REST con
// repAxios (token del rappresentante); la generazione in streaming passa da leggiSse.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export type StatoAudio = 'in_registrazione' | 'in_caricamento' | 'completa' | 'in_trascrizione' | 'trascritta' | 'errore';
export type OrigineAudio = 'registrazione' | 'file';

export interface RiepilogoAudio {
  _id: string;
  ordine: number;
  origine: OrigineAudio;
  nomeOriginale: string | null;
  stato: StatoAudio;
  /**
   * Registrazione senza pezzi da più di 3 minuti (10 minuti senza pezzi né battiti per quelle
   * dell'app): «Registrazione interrotta alle …».
   */
  interrottaAlle: string | null;
  /** 'app' = registrata dal telefono (assente nei backend di prima). */
  dispositivo?: 'app' | null;
  /** Ultimo segnale del telefono (ogni 60 s, anche in pausa). */
  ultimoBattitoAt?: string | null;
  durataMs: number | null;
  dimensione: number;
  dimensioneDichiarata: number | null;
  riprese: { indice: number; pezzi: number; durataMs: number | null }[];
  avanzamento: { fatti: number; totali: number };
  errore: { codice: string; messaggio: string; at: string | null } | null;
  scadeIl: string | null;
  eliminatoAt: string | null;
  senzaVoci: boolean;
  /** Lavoro fermato dalla scadenza a metà: la trascrizione dei tratti fatti resta (stato `errore`). */
  trascrizioneParziale?: boolean;
  vociIncerte: number;
  /** URL firmato RELATIVO (`/api/verbali-audio/...`): usare urlAssolutoAudio. */
  urlAscolto: string | null;
  urlDownload: { ripresa: number; url: string }[];
}

/** Voce salvata nel verbale (`verbale.voci`). */
export interface VoceVerbale {
  audioId: string | null;
  etichetta: string;
  nome: string;
  invitationId: string | null;
  uguale: string | null;
}

/** Riga di `GET /voci`: voce di un audio trascritto con i dati per riconoscerla. */
export interface RiepilogoVoce {
  audioId: string;
  etichetta: string;
  nome: string | null;
  invitationId: string | null;
  uguale: string | null;
  certa: boolean;
  minuti: number;
  nomeMostrato: string;
  esempi: { daMs: number; testo: string }[];
}

export interface CandidatoVoce {
  nome: string;
  dettaglio: string;
  invitationId?: string | null;
}

/** Corpo di `PUT /voci` (una voce). */
export interface VoceDaSalvare {
  audioId: string;
  etichetta: string;
  nome?: string;
  invitationId?: string | null;
  uguale?: string | null;
}

export interface Segnaposto {
  _id: string;
  audioId: string | null;
  ripresa: number;
  msRipresa: number;
  ms: number | null;
  etichetta: string;
  createdAt: string | null;
}

export interface TettiAudio {
  minuti: number;
  minutiUsati: number;
  generazioni: number;
  generazioniUsate: number;
}

/** `data` di `GET /audio`. */
export interface DatiAudioVerbale {
  audio: RiepilogoAudio[];
  voci: VoceVerbale[];
  segnaposti: Segnaposto[];
  tetti: TettiAudio;
  puoRegistrare: boolean;
}

export interface SegmentoTrascrizione {
  voce: string | null;
  nome: string;
  daMs: number;
  aMs: number;
  testo: string;
  tratto: number | null;
  certa: boolean;
}

export interface TrascrizioneAudio {
  segmenti: SegmentoTrascrizione[];
  senzaVoci: boolean;
  /** Trascrizione dei soli tratti fatti prima della scadenza dell'audio. */
  parziale?: boolean;
  durataMs: number | null;
}

export interface CorpoNuovoAudio {
  origine: OrigineAudio;
  avvisoConfermato: boolean;
  mime?: string;
  nomeOriginale?: string;
  dimensione?: number;
  impronta?: string;
}

export interface CorpoTermina {
  riprese?: { indice: number; pezzi: number }[];
  accettaMancanti?: boolean;
}

export interface PezzoMancante {
  ripresa: number;
  indice: number;
}

const percorsoVerbale = (eventId: string) => `/representative/events/${eventId}/verbale`;
const percorsoAudio = (eventId: string, audioId: string) => `${percorsoVerbale(eventId)}/audio/${audioId}`;

/** Origine delle API senza `/api` finale (es. `https://studi.unasca.it`). */
const ORIGINE_API = API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');

/** URL firmato dell'audio (relativo `/api/verbali-audio/...`) ⇒ URL assoluto verso il backend. */
export function urlAssolutoAudio(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${ORIGINE_API}${url.startsWith('/') ? '' : '/'}${url}`;
}

export interface DettaglioErrore {
  /** Stato HTTP; null se la richiesta non ha avuto risposta (rete). */
  status: number | null;
  messaggio: string;
  codice: string | null;
  rete: boolean;
}

const MESSAGGIO_RETE = 'Connessione assente: riprova quando torna la rete';

/** Errore di axios (o simile) ⇒ stato, messaggio del server e codice. */
export function erroreRichiesta(err: any): DettaglioErrore {
  const status = typeof err?.response?.status === 'number' ? err.response.status : null;
  if (status === null) return { status: null, messaggio: MESSAGGIO_RETE, codice: null, rete: true };
  const dati = err.response.data;
  const messaggio = (dati && typeof dati.error === 'string' && dati.error) || 'Errore del server';
  const codice = (dati && typeof dati.codice === 'string' && dati.codice) || null;
  return { status, messaggio, codice, rete: false };
}

export const elencaAudio = async (eventId: string): Promise<DatiAudioVerbale> => {
  const { data } = await repAxios.get(`${percorsoVerbale(eventId)}/audio`);
  return data.data;
};

/** Nuovo audio (o ripresa di un caricamento interrotto con la stessa impronta: `pezziRicevuti`). */
export const creaAudio = async (
  eventId: string,
  corpo: CorpoNuovoAudio
): Promise<{ audio: RiepilogoAudio; pezziRicevuti: number[] }> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/audio`, corpo);
  return { audio: data.audio, pezziRicevuti: Array.isArray(data.pezziRicevuti) ? data.pezziRicevuti : [] };
};

/** Nuova ripresa della stessa registrazione (201) ⇒ indice della ripresa. */
export const nuovaRipresa = async (eventId: string, audioId: string, mime?: string): Promise<number> => {
  const { data } = await repAxios.post(`${percorsoAudio(eventId, audioId)}/riprese`, mime ? { mime } : {});
  return data.ripresa;
};

/** Un pezzo (≤ 8 MiB), idempotente per indice. */
export const inviaPezzo = async (
  eventId: string,
  audioId: string,
  ripresa: number,
  indice: number,
  blob: Blob,
  segnale?: AbortSignal
): Promise<{ gia: boolean }> => {
  const { data } = await repAxios.put(`${percorsoAudio(eventId, audioId)}/riprese/${ripresa}/pezzi/${indice}`, blob, {
    headers: { 'Content-Type': 'application/octet-stream' },
    signal: segnale,
  });
  return { gia: !!data?.gia };
};

/** «Termina»: ricompone e mette in coda di trascrizione (409 PEZZI_MANCANTI con `mancanti`). */
export const terminaAudio = async (
  eventId: string,
  audioId: string,
  corpo: CorpoTermina = {}
): Promise<{ audio: RiepilogoAudio; mancanti: PezzoMancante[] }> => {
  const { data } = await repAxios.post(`${percorsoAudio(eventId, audioId)}/termina`, corpo);
  return { audio: data.audio, mancanti: Array.isArray(data.mancanti) ? data.mancanti : [] };
};

/** Rimette in coda un audio completo o in errore. */
export const trascriviAudio = async (eventId: string, audioId: string): Promise<RiepilogoAudio> => {
  const { data } = await repAxios.post(`${percorsoAudio(eventId, audioId)}/trascrivi`);
  return data.audio;
};

export const eliminaAudio = async (eventId: string, audioId: string): Promise<void> => {
  await repAxios.delete(percorsoAudio(eventId, audioId));
};

export const leggiTrascrizione = async (eventId: string, audioId: string): Promise<TrascrizioneAudio> => {
  const { data } = await repAxios.get(`${percorsoAudio(eventId, audioId)}/trascrizione`);
  return {
    segmenti: Array.isArray(data.segmenti) ? data.segmenti : [],
    senzaVoci: !!data.senzaVoci,
    parziale: !!data.parziale,
    durataMs: data.durataMs ?? null,
  };
};

export const aggiungiSegnaposto = async (
  eventId: string,
  corpo: { audioId: string; ripresa: number; msRipresa: number; etichetta: string }
): Promise<Segnaposto> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/segnaposti`, corpo);
  return data.segnaposto;
};

export const eliminaSegnaposto = async (eventId: string, segnapostoId: string): Promise<void> => {
  await repAxios.delete(`${percorsoVerbale(eventId)}/segnaposti/${segnapostoId}`);
};

export const leggiVoci = async (eventId: string): Promise<{ voci: RiepilogoVoce[]; candidati: CandidatoVoce[] }> => {
  const { data } = await repAxios.get(`${percorsoVerbale(eventId)}/voci`);
  return {
    voci: Array.isArray(data.voci) ? data.voci : [],
    candidati: Array.isArray(data.candidati) ? data.candidati : [],
  };
};

/** Sostituisce le voci del verbale ⇒ le voci salvate (quelle restituite dal server, se ci sono). */
export const salvaVoci = async (eventId: string, voci: VoceDaSalvare[]): Promise<Array<VoceVerbale | VoceDaSalvare>> => {
  const { data } = await repAxios.put(`${percorsoVerbale(eventId)}/voci`, { voci });
  return Array.isArray(data?.voci) ? data.voci : voci;
};

/** Riscrive una parte del verbale basandosi sulla trascrizione (non salva nulla). */
export const riprendiDallaTrascrizione = async (eventId: string, html: string): Promise<string> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/riprendi-trascrizione`, { html });
  return data.html;
};

/**
 * Verbale dalla trascrizione in SSE: `evento('sezione', {chiave, titolo})`,
 * `delta(testo)`, `evento('sezioneFine', {chiave, html})`, `fine({contenuto, riferimenti, ultimaModifica})`.
 */
export const generaVerbale = (
  eventId: string,
  { sezione }: { sezione?: string },
  gestori: GestoriSse,
  segnale?: AbortSignal
) => leggiSse(`${percorsoVerbale(eventId)}/genera`, sezione ? { sezione } : {}, gestori, segnale);
